import { Injectable } from '@angular/core';
import { HeatloadStore } from '../../state/heatload/heatload.store';
import { TranslateService } from '@ngx-translate/core';
import { ProjectStore } from '../../state/project/project.store';
import { withLatestFrom, map } from 'rxjs';
import { CountryCode, EnergyCertMeasurementUnits, FormName, HeatLoadCalculationMethod } from '@nx-customer-apps/shared/enums';
import { EnergyCertificateFormValue } from '@nx-customer-apps/shared/interfaces';
import { HeatloadFormOptionsService } from './heatload-form-options.service';
import { HeatloadPrefillMapper } from '../../services';
import { AccordionService } from '../../shared/interfaces';
import { EnergyCertificateFormService } from '../../shared/components/energy-certificate';

@Injectable({
    providedIn: 'root'
})
export class EnergyCertificateService implements AccordionService {
    constructor(
        private prefillMapper: HeatloadPrefillMapper,
        private heatloadFormOptionsService: HeatloadFormOptionsService,
        private heatloadStore: HeatloadStore,
        private translate: TranslateService,
        private projectStore: ProjectStore,
        private formService: EnergyCertificateFormService
    ) {}

    public questions$ = this.heatloadStore.calculationMethod$.pipe(
        withLatestFrom(this.projectStore.project$, this.heatloadStore.cache$),
        map(([method, project, cache]) => ({
            isEnergyCertificate: method?.value === HeatLoadCalculationMethod.EnergyCertificate,
            project,
            cache
        })),
        map(state => {
            if (!state.isEnergyCertificate) {
                return null;
            }

            const projectPrefill = this.prefillMapper.projectToEnergyCertificatePrefill(state.project);
            const cachePrefill = this.prefillMapper.stateToEnergyCertificatePrefill(state.cache?.energyCertificate);
            const formOptions = this.heatloadFormOptionsService.getEnergyCertificateFormOptions({
                countryCode: state.project!.address.countryCode as CountryCode,
                prefill: cachePrefill || projectPrefill
            });
            return this.formService.getEnergyCertificateQuestions(formOptions);
        })
    );

    public getSuccessMessage(formValue: EnergyCertificateFormValue): string {
        return Object.entries(formValue)
            .map(value => {
                const result = value[1];
                const unitTranslationKey = EnergyCertMeasurementUnits[value[0] as keyof typeof EnergyCertMeasurementUnits];
                return `${result} ${this.translate.instant(unitTranslationKey)}`;
            })
            .join(', ');
    }

    public isExpanded$ = this.isSubmitedCalculationMethodSameValue$();

    private isSubmitedCalculationMethodSameValue$() {
        return this.heatloadStore.sameValueSubmited$.pipe(
            map(
                form =>
                    form?.formName === FormName.CalculationMethod &&
                    form.value.methods.standardMethod === HeatLoadCalculationMethod.EnergyCertificate
            )
        );
    }
}
